import { useSession } from '../store/session';
import { LearnerDashboard } from './LearnerDashboard';
import { FacilitatorDashboard } from './FacilitatorDashboard';
import { SchoolAdminDashboard } from './SchoolAdminDashboard';
import { MinistryDashboard } from './MinistryDashboard';
import { ContentDeveloperDashboard } from './ContentDeveloperDashboard';

/**
 * Which dashboard to show.
 *
 * The rule is worth stating because it is the whole persona/role split in five
 * lines of code: **permission decides what is possible, persona decides what is
 * shown first.** A ministry analyst and a head teacher hold different grants,
 * so they get different dashboards. A teacher and a learner hold *identical*
 * grants and get different dashboards — from persona, which grants nothing.
 *
 * The outline asks for nine dashboards. Five cover them: Super Administrator is
 * an `ORG_ADMIN` with `*@*` on a platform org, Institutional Administrator and
 * Ministry are the same subtree view at different depths, and Student and
 * Teacher differ by persona rather than by anything structural.
 */
export function DashboardRouter() {
  const { profile, permissions } = useSession();

  const has = (permission: string) =>
    permissions.includes('*@*') || permissions.includes(permission);

  if (has('analytics@read_subtree')) return <MinistryDashboard />;
  if (has('analytics@read_org') || has('member@invite')) return <SchoolAdminDashboard />;
  if (has('assessment@grade') || has('enrollment@assign')) return <FacilitatorDashboard />;
  if (has('course@create') && profile?.persona === 'curriculum_officer') {
    return <ContentDeveloperDashboard />;
  }
  if (has('course@create')) return <ContentDeveloperDashboard />;

  return <LearnerDashboard />;
}
